import React, { useState, useEffect, useCallback } from 'react';

// Tour guidato "esplicativo": oscura lo schermo e illumina un elemento alla volta (spotlight).
// Il cartellino spiega il passo ed e' SEMPRE ancorato in fondo (non salta tra un passo e l'altro).
// Ogni step: { target?, title, body, onEnter?, demo? }.
//  - target -> illumina l'elemento; senza target -> solo schermo scurito.
//  - demo: 'cov' | 'luc' -> mostra una finestra di DETTAGLIO FAKE (dati d'esempio) con l'icona
//    a occhio evidenziata, per far capire cosa apre quell'icona sul risultato.

const EyeIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

// Righe/chip d'esempio per la finestra fake
const DEMO_COV = [['FWA 5G', true], ['Fibra FTTH', true], ['xDSL', false], ['ADSL', true], ['Fibra dedicata', false]];
const DEMO_LUC = [['Azienda', 'P.IVA presente'], ['Fisso', '2 linee'], ['Mobile', '5 SIM'], ['ICT', '1 servizio']];

function DemoDettaglio({ tipo }) {
  const wrap = {
    position: 'fixed', left: 16, right: 16, top: 'max(9vh, calc(12px + env(safe-area-inset-top)))',
    zIndex: 2, maxWidth: 420, margin: '0 auto',
    background: 'linear-gradient(180deg,var(--panel),var(--panel2))', border: '1px solid var(--line-2)',
    borderRadius: 22, padding: '16px 16px 14px', boxShadow: '0 30px 70px -22px rgba(0,0,0,.6)',
    color: 'var(--ink)',
  };
  const head = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 };
  const eyeBadge = {
    width: 38, height: 38, flex: '0 0 auto', borderRadius: 12, display: 'grid', placeItems: 'center',
    color: '#04121a', background: 'var(--c456)', boxShadow: '0 0 0 4px rgba(56,224,200,.28)',
    animation: 'pdblink 1.4s ease-in-out infinite',
  };
  const okChip = (ok) => ({
    fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 999,
    color: ok ? '#34d399' : 'var(--faint)',
    background: ok ? 'rgba(52,211,153,.14)' : 'var(--surface)',
    border: `1px solid ${ok ? 'rgba(52,211,153,.3)' : 'var(--line)'}`,
  });
  const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--line)', fontSize: 13 };

  return (
    <div style={wrap}>
      <div style={head}>
        <div style={eyeBadge}><EyeIcon size={19} /></div>
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 800 }}>{tipo === 'cov' ? 'Dettaglio copertura' : 'Dettaglio presidio'} <span style={{ color: 'var(--faint)', fontWeight: 600 }}>· esempio</span></div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>Si apre toccando l'icona a occhio sul risultato</div>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        {tipo === 'cov'
          ? DEMO_COV.map(([nome, ok], k) => (
              <div key={k} style={{ ...row, borderBottom: k === DEMO_COV.length - 1 ? 'none' : row.borderBottom }}>
                <span style={{ fontWeight: 650 }}>{nome}</span>
                <span style={okChip(ok)}>{ok ? 'Disponibile' : 'No'}</span>
              </div>
            ))
          : (
            <>
              <div style={{ ...row, borderBottom: 'none', paddingBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Cliente</span><span style={{ fontWeight: 750 }}>ACME S.R.L.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                {DEMO_LUC.map(([cat, val], k) => (
                  <div key={k} style={{ border: '1px solid var(--line)', borderRadius: 12, padding: '9px 11px', background: 'var(--surface)' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em' }}>{cat}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <EyeIcon size={14} /> e lo screenshot del portale SalesMate
              </div>
            </>
          )}
      </div>
    </div>
  );
}

// Finestra FAKE che mostra i campi copertura che si auto-compilano (Comune -> Via -> Civico),
// per far capire che l'indirizzo si completa dai suggerimenti. Chiama onDone quando ha finito.
function DemoFill({ onDone }) {
  const campi = [['Comune', 'MILANO'], ['Via', 'VIA ROMA'], ['Civico', '10']];
  const [n, setN] = useState(0);
  useEffect(() => {
    const timers = campi.map((_, k) => setTimeout(() => setN(k + 1), (k + 1) * 850));
    timers.push(setTimeout(() => { if (onDone) onDone(); }, campi.length * 850 + 500));
    return () => timers.forEach(clearTimeout);
  }, []); // una sola volta al mount del passo
  const wrap = {
    position: 'fixed', left: 16, right: 16, top: 'max(9vh, calc(12px + env(safe-area-inset-top)))',
    zIndex: 2, maxWidth: 420, margin: '0 auto',
    background: 'linear-gradient(180deg,var(--panel),var(--panel2))', border: '1px solid var(--line-2)',
    borderRadius: 22, padding: '16px', boxShadow: '0 30px 70px -22px rgba(0,0,0,.6)', color: 'var(--ink)',
  };
  const field = (done) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', marginTop: 9,
    borderRadius: 14, background: 'var(--input-bg)',
    border: `1px solid ${done ? 'var(--ccov)' : 'var(--line)'}`, transition: 'border-color .3s',
  });
  return (
    <div style={wrap}>
      <div style={{ fontSize: 14.5, fontWeight: 800 }}>Copertura <span style={{ color: 'var(--faint)', fontWeight: 600 }}>· esempio</span></div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>I campi si completano dai suggerimenti mentre scrivi</div>
      {campi.map(([label, val], k) => {
        const done = k < n;
        return (
          <div key={k} style={field(done)}>
            <span style={{ fontSize: 10.5, color: 'var(--faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', width: 54, flex: '0 0 auto' }}>{label}</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: done ? 700 : 400, color: done ? 'var(--ink)' : 'var(--faint)' }}>{done ? val : 'digita…'}</span>
            {done && (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--ccov)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            )}
          </div>
        );
      })}
    </div>
  );
}

const ACT = {
  num: { c: 'var(--c456)', soft: 'rgba(56,224,200,.14)', line: 'rgba(56,224,200,.32)', t: 'Verifica numero' },
  cov: { c: 'var(--ccov)', soft: 'rgba(91,140,255,.14)', line: 'rgba(91,140,255,.32)', t: 'Copertura SSMATE' },
  luc: { c: '#ffc95b', soft: 'rgba(255,201,91,.14)', line: 'rgba(255,201,91,.32)', t: 'Lucchetto SSMATE' },
};

// Finestra FAKE del "sistema notifiche": la bollicina che raccoglie le verifiche (in corso +
// completate). Statica, solo per spiegare dove finiscono i risultati.
function DemoNotif() {
  const wrap = {
    position: 'fixed', left: 16, right: 16, top: 'max(9vh, calc(12px + env(safe-area-inset-top)))',
    zIndex: 2, maxWidth: 420, margin: '0 auto',
    background: 'linear-gradient(180deg,var(--panel),var(--panel2))', border: '1px solid var(--line-2)',
    borderRadius: 22, padding: '14px', boxShadow: '0 30px 70px -22px rgba(0,0,0,.6)', color: 'var(--ink)',
  };
  const righe = [
    { tipo: 'cov', wip: true, sub: '60% · in corso' },
    { tipo: 'num', wip: false, sub: 'TIM' },
  ];
  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ position: 'relative', width: 44, height: 44, flex: '0 0 auto', borderRadius: '50%',
          background: 'linear-gradient(180deg,var(--panel),var(--panel2))', border: '1px solid var(--line-2)',
          display: 'grid', placeItems: 'center', color: 'var(--c456)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
          <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 20, height: 20, padding: '0 5px', borderRadius: 999, background: 'var(--c456)', color: '#04121a', fontSize: 12, fontWeight: 800, display: 'grid', placeItems: 'center', boxShadow: '0 0 0 2px var(--panel)' }}>2</span>
          <span style={{ position: 'absolute', top: 1, left: 1, width: 11, height: 11, borderRadius: '50%', background: 'var(--cvf)', boxShadow: '0 0 8px var(--cvf)', animation: 'pdblink 1.4s infinite' }} />
        </div>
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 800 }}>Notifiche <span style={{ color: 'var(--faint)', fontWeight: 600 }}>· esempio</span></div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>Le verifiche finiscono qui</div>
        </div>
      </div>
      {righe.map((r, k) => {
        const a = ACT[r.tipo];
        return (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 4px', borderTop: k ? '1px solid var(--line)' : 'none' }}>
            <div style={{ width: 30, height: 30, flex: '0 0 auto', borderRadius: 9, display: 'grid', placeItems: 'center', color: a.c, background: a.soft, border: `1px solid ${a.line}` }}>
              {r.wip
                ? <span style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${a.soft}`, borderTopColor: a.c, display: 'block', animation: 'pdspin .9s linear infinite' }} />
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{a.t}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.sub}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TourGuidato({ steps, onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [locked, setLocked] = useState(false);   // 'Avanti' bloccato durante un'animazione del passo
  const step = steps[i];

  const misura = useCallback(() => {
    const s = steps[i];
    if (!s || !s.target) { setRect(null); return; }
    const el = document.querySelector(s.target);
    if (!el) { setRect(null); return; }
    el.scrollIntoView && el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [i, steps]);

  // Cambio passo: esegui onEnter (es. cambio tab), poi misura con un piccolo ritardo per le transizioni.
  useEffect(() => {
    if (!step) return;
    // Un passo con holdNext blocca 'Avanti' finche' l'animazione non chiama lo sblocco.
    setLocked(!!step.holdNext);
    if (step.onEnter) step.onEnter(step.holdNext ? () => setLocked(false) : undefined);
    const t = setTimeout(misura, step.onEnter ? 400 : 80);
    return () => clearTimeout(t);
  }, [i]);

  useEffect(() => {
    const h = () => misura();
    window.addEventListener('resize', h);
    window.addEventListener('scroll', h, true);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('scroll', h, true); };
  }, [misura]);

  // Auto-avanti: quando il passo e' sbloccato (e non e' l'ultimo), dopo 20s passa da solo al
  // successivo (tempo per leggere). Il tasto 'Avanti' si riempie di colore in 20s da conto alla rovescia.
  useEffect(() => {
    if (locked || i >= steps.length - 1) return undefined;
    const t = setTimeout(() => setI(v => v + 1), 20000);
    return () => clearTimeout(t);
  }, [i, locked, steps.length]);

  if (!step) return null;
  const last = i === steps.length - 1;
  const pad = 8;
  const hole = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;

  // Cartellino SEMPRE in fondo: lo schermo resta fermo, cambia solo la parte illuminata sopra.
  const cardStyle = { top: 'auto', transform: 'none', left: 16, right: 16, bottom: 'calc(16px + env(safe-area-inset-bottom))' };

  return (
    <div className="tour">
      <div className="tour-catch" style={{ background: rect ? 'transparent' : 'rgba(4,8,18,.74)' }} />
      {hole && <div className="tour-hole" style={hole} />}
      {step.demo === 'fill' ? <DemoFill onDone={() => setLocked(false)} />
        : step.demo === 'notif' ? <DemoNotif />
        : step.demo ? <DemoDettaglio tipo={step.demo} /> : null}
      <div className="tour-card" style={cardStyle}>
        <div className="tour-t">{step.title}</div>
        <div className="tour-b">{step.body}</div>
        <div className="tour-foot">
          <div className="tour-dots">{steps.map((_, k) => <span key={k} className={k === i ? 'on' : ''} />)}</div>
          <div className="tour-btns">
            {!last && <button className="tour-skip" onClick={onClose}>Salta</button>}
            {i > 0 && <button className="tour-prev" onClick={() => setI(i - 1)}>Indietro</button>}
            <button className="tour-next" disabled={locked} style={locked ? { opacity: .5, cursor: 'default' } : undefined}
              onClick={() => { if (locked) return; last ? onClose() : setI(i + 1); }}>
              {!locked && !last && <span key={i} className="tour-fill" />}
              <span className="tour-next-lbl">{last ? 'Fine' : 'Avanti'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
